import React, { useState, useEffect } from 'react';
import {
    Bed, Building, Building2, Camera, Castle,
    Clapperboard,
    Dumbbell,
    Film,
    Gem, Home, Hotel, Key, Library,
    Loader2, MapPin, Mountain, Palette, Palmtree,
    PartyPopper, Snowflake, Sprout, Tent, Tractor,
    TreePine, Warehouse, X
} from 'lucide-react';
import type { MantenedorRecord, MantenedorTab } from '../types';

// Diccionario de iconos disponibles
const AVAILABLE_ICONS: Record<string, React.ComponentType<any>> = {
    Home,
    Building,
    Building2,
    Hotel,
    Warehouse,
    Tent,
    Palmtree,
    Mountain,
    TreePine,
    Castle,
    Gem,
    Camera,
    Library,
    PartyPopper,
    Snowflake,
    Tractor,
    Sprout,
    Palette,
    Bed,
    Key,
    MapPin,
    Dumbbell,
    Clapperboard,
    Film
};

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    editingRecord: MantenedorRecord | null;
    activeTab: MantenedorTab;
    onSave: (formData: any) => void;
    isSaving?: boolean;
}

export const MantenedorFormModal: React.FC<ModalProps> = ({
    isOpen, onClose, editingRecord, activeTab, onSave, isSaving
}) => {
    const [formData, setFormData] = useState({
        propertyTypeId: 1,
        code: '',
        name: '',
        description: '',
        icon: 'Home',
    });

    useEffect(() => {
        if (isOpen) {
            if (editingRecord) {
                setFormData({
                    propertyTypeId: Number(editingRecord.propertyTypeId) || 1,
                    code: editingRecord.code || '',
                    name: editingRecord.name || '',
                    description: editingRecord.description || '',
                    icon: editingRecord.icon || 'Home',
                });
            } else {
                // Resetear a valores iniciales si es creación
                setFormData({
                    propertyTypeId: 1,
                    code: '',
                    name: '',
                    description: '',
                    icon: 'Home',
                });
            }
        }
    }, [isOpen, editingRecord]);

    if (!isOpen) return null;

    const isEditing = !!editingRecord;
    const tabName = activeTab === 'propiedad' ? 'Propiedad' : 'Alojamiento';

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            // Conversión a número si el campo es propertyTypeId
            [name]: name === 'propertyTypeId' ? Number(value) : value,
        }));
    };

    // Función para cambiar el icono
    const handleIconSelect = (iconName: string) => {
        setFormData((prev) => ({
            ...prev,
            icon: iconName
        }));
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">

                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <h3 className="text-base font-bold text-gray-900">
                        {isEditing ? `Editar Tipo de ${tabName}` : `Crear Tipo de ${tabName}`}
                    </h3>
                    <button type="button" onClick={onClose} className="text-gray-400 transition-colors hover:text-gray-700">
                        <X size={20} />
                    </button>
                </div>

                {/* --- INICIO DEL FORMULARIO --- */}
                <form onSubmit={handleSubmit} className="flex flex-col">
                    <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto hide-scrollbar">

                        {/* SELECT DE TIPO DE PROPIEDAD */}
                        {activeTab === 'propiedad' && (
                            <div>
                                <label className="block mb-1.5 text-[12px] font-bold tracking-wide text-gray-600 uppercase">
                                    Tipo de Propiedad
                                </label>
                                <select
                                    name="propertyTypeId"
                                    required
                                    value={formData.propertyTypeId}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg outline-none focus:border-[#4f6df5] focus:ring-1 focus:ring-[#4f6df5]/20 bg-white"
                                >
                                    <option value={1}>Tipo 1 (Ej. Residencial)</option>
                                    <option value={2}>Tipo 2 (Ej. Comercial)</option>
                                </select>
                            </div>
                        )}

                        {/* CONTENEDOR PRINCIPAL*/}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">
                                Seleccionar Ícono para la Propiedad
                            </label>

                            {/* ÍCONO */}
                            <div className="max-h-[240px] overflow-y-auto pr-2 border border-gray-200 rounded-lg bg-gray-50/50 p-3 shadow-inner">
                                <div className="grid grid-cols-5 gap-3">
                                    {Object.keys(AVAILABLE_ICONS).map((iconName) => {
                                        const IconComponent = AVAILABLE_ICONS[iconName];
                                        const isSelected = formData.icon === iconName;

                                        return (
                                            <button
                                                key={iconName}
                                                type="button"
                                                onClick={() => handleIconSelect(iconName)}
                                                className={`
                                                    flex flex-col items-center justify-center p-2.5 rounded-xl transition-all duration-200 group relative
                                                    ${isSelected
                                                        ? 'bg-[#4f6df5] text-white shadow-md shadow-[#4f6df5]/20 scale-105'
                                                        : 'bg-white hover:bg-gray-100 text-gray-600 border border-gray-100 hover:border-gray-300'
                                                    }
                                                `}
                                                title={iconName}
                                            >
                                                <div className="flex items-center justify-center w-6 h-6">
                                                    <IconComponent size={20} strokeWidth={isSelected ? 2.5 : 2} />
                                                </div>

                                                {/* Texto debajo del ícono para saber cuál es */}
                                                <span className={`text-[10px] mt-1.5 truncate w-full text-center block ${isSelected ? 'text-white font-medium' : 'text-gray-400 group-hover:text-gray-600'}`}>
                                                    {iconName}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Texto de ayuda */}
                            <p className="text-[11px] text-gray-400 italic">
                                Desplázate hacia abajo para ver el catálogo completo ({Object.keys(AVAILABLE_ICONS).length} íconos disponibles).
                            </p>
                        </div>

                        <div>
                            <label className="block mb-1.5 text-[12px] font-bold tracking-wide text-gray-600 uppercase">
                                Código
                            </label>
                            <input
                                type="text"
                                name="code"
                                required
                                value={formData.code}
                                onChange={handleChange}
                                placeholder={activeTab === 'propiedad' ? "Ej. COUNTRY" : "Ej. ENTIRE"}
                                className="w-full px-3 py-2 font-mono text-[13px] border border-gray-300 rounded-lg outline-none focus:border-[#4f6df5] focus:ring-1 focus:ring-[#4f6df5]/20 uppercase"
                            />
                        </div>

                        <div>
                            <label className="block mb-1.5 text-[12px] font-bold tracking-wide text-gray-600 uppercase">
                                Nombre
                            </label>
                            <input
                                type="text"
                                name="name"
                                required
                                value={formData.name}
                                onChange={handleChange}
                                placeholder={activeTab === 'propiedad' ? "Ej. Casas rurales" : "Ej. Alojamiento entero"}
                                className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg outline-none focus:border-[#4f6df5] focus:ring-1 focus:ring-[#4f6df5]/20"
                            />
                        </div>

                        <div>
                            <label className="block mb-1.5 text-[12px] font-bold tracking-wide text-gray-600 uppercase">
                                Descripción
                            </label>
                            <textarea
                                name="description"
                                required
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Añade una descripción clara..."
                                rows={3}
                                className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-lg outline-none focus:border-[#4f6df5] focus:ring-1 focus:ring-[#4f6df5]/20 resize-none"
                            />
                        </div>

                    </div>

                    <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSaving}
                            className="px-4 py-2 text-[13px] font-semibold text-gray-600 transition-colors bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="flex items-center gap-2 px-4 py-2 text-[13px] font-semibold text-white transition-colors bg-[#4f6df5] rounded-lg hover:bg-[#3f58c7]"
                        >
                            {isSaving && <Loader2 size={14} className="animate-spin" />}
                            {isSaving ? 'Guardando...' : 'Guardar cambios'}
                        </button>
                    </div>
                </form>

            </div>
        </div>
    );
};